import type { FC, PropsWithChildren } from 'react'
import { useEffect } from 'react'

import { useLatest } from '../../hooks'
import { setCanvasSize } from '../../utils'
import { useWebGlConstructContext, WebGlContext } from './context'
import { prepareRendering } from './helpers'
import { useCheckRenders, useLazyRender } from './hooks'
import css from './WebGlCanvas.css'

type WebGlCanvasProps = {
  width: number
  height: number
}

export const WebGlCanvas: FC<PropsWithChildren<WebGlCanvasProps>> = ({
  width,
  height,
  children,
}) => {
  const context = useWebGlConstructContext()
  const {
    canvas: { value: containerElement, setValue: setContainerElement },
    renderQueue: { value: renders },
    update: { value: update, setValue: setUpdate },
  } = context

  const canvasRef = useLatest(containerElement)

  prepareRendering(renders)

  useCheckRenders(renders, setUpdate)
  useLazyRender(canvasRef, renders, update)

  useEffect(() => {
    if (!containerElement) return

    setCanvasSize(containerElement, width, height, window.devicePixelRatio)
  }, [width, height, containerElement])

  return (
    <WebGlContext.Provider value={context}>
      <canvas
        ref={setContainerElement}
        className={css.canvasContainer}
        width={width}
        height={height}
      />
      {children}
    </WebGlContext.Provider>
  )
}
